import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Power, Music2 } from "lucide-react";
import { api } from "../lib/api";
import { Bini } from "../components/Bini";
import { YouTubePlayer } from "../components/YouTubePlayer";
import { Footer } from "../components/Footer";
import { t } from "../i18n";

const TIMERS = [5, 10, 15];

const NightSky = () => (
  <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
    {[[8, 12], [22, 30], [70, 10], [85, 26], [55, 18], [40, 6], [92, 50], [12, 60], [30, 80], [78, 72]].map(([x, y], i) => (
      <span key={i} className="absolute rounded-full bg-[#F4E3A0]" style={{ left: `${x}%`, top: `${y}%`, width: 3, height: 3, opacity: 0.5 }} />
    ))}
    <div className="absolute right-6 top-20 sm:right-16 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-[#F4E3A0] opacity-80"
      style={{ boxShadow: "0 0 60px rgba(244,227,160,0.25)" }}>
      <div className="absolute -right-3 -top-3 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-[#0B132B]" />
    </div>
  </div>
);

const Goodnight = ({ onAgain }) => (
  <div data-testid="dodo-goodnight" className="flex flex-col items-center text-center pt-16">
    <div className="flex items-end gap-3">
      <Bini size={140} mood="soft" className="anim-wave origin-bottom" data-testid="dodo-bini-wave" />
      <div data-testid="dodo-goodnight-bubble" className="mb-16 rounded-[24px] bg-[#1C2541] px-4 py-3 text-lg text-[#F4E3A0]/90">{t("dodo.goodnightBubble")}</div>
    </div>
    <h2 className="mt-6 text-4xl font-extrabold text-[#F4E3A0]">{t("dodo.goodnight")}</h2>
    <p className="mt-2 text-lg text-white/60">{t("dodo.goodnightSub")}</p>
    <button type="button" onClick={onAgain} data-testid="dodo-again-btn" className="mt-10 rounded-full border-2 border-white/20 px-6 min-h-[56px] text-white/70 font-semibold">
      {t("dodo.again")}
    </button>
  </div>
);

export default function Dodo() {
  const [songs, setSongs] = useState([]);
  const [current, setCurrent] = useState(null);
  const [minutes, setMinutes] = useState(10);
  const [stopAt, setStopAt] = useState(null);
  const [asleep, setAsleep] = useState(false);
  const [humming, setHumming] = useState(false);
  const playerRef = useRef(null);

  useEffect(() => { api.get("/songs", { params: { calm: true } }).then((r) => setSongs(r.data)); }, []);

  // Bini's quiet lullaby bubble: static, gone after a few seconds
  useEffect(() => {
    if (!current) return setHumming(false);
    setHumming(true);
    const id = setTimeout(() => setHumming(false), 8000);
    return () => clearTimeout(id);
  }, [current]);

  useEffect(() => {
    if (!stopAt) return;
    const id = setTimeout(() => {
      playerRef.current?.stopVideo?.();
      setCurrent(null);
      setStopAt(null);
      setAsleep(true);
    }, stopAt - Date.now());
    return () => clearTimeout(id);
  }, [stopAt]);

  const start = (song) => { setCurrent(song); setAsleep(false); setStopAt(Date.now() + minutes * 60000); };
  const pickTimer = (m) => { setMinutes(m); if (current) setStopAt(Date.now() + m * 60000); };
  const stopTime = stopAt && new Date(stopAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="night-bg relative text-white overflow-x-hidden">
      <NightSky />
      <header className="relative z-10 flex items-center justify-between px-4 sm:px-8 pt-4">
        <Link to="/" data-testid="dodo-leave-btn" aria-label={t("dodo.leave")}
          className="grid place-items-center w-14 h-14 rounded-full border-2 border-white/20 text-white/70">
          <ArrowLeft className="w-7 h-7" />
        </Link>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#F4E3A0]/90">{t("dodo.title")}</h1>
        <span className="w-14" />
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-4 sm:px-8 pb-16 pt-6">
        {asleep ? <Goodnight onAgain={() => setAsleep(false)} /> : (
          <>
            {current ? (
              <div className="space-y-3">
                {(
                  <div data-testid="dodo-lullaby-bubble" data-visible={humming} className={`flex items-center gap-3 ${humming ? "" : "invisible"}`}>
                    <Bini size={44} mood="sleep" />
                    <p className="rounded-[20px] bg-[#1C2541] px-4 py-2 text-base text-[#F4E3A0]/80">
                      <Music2 className="mr-1.5 inline h-4 w-4 opacity-70" aria-hidden="true" />{t("dodo.hum")}
                    </p>
                  </div>
                )}
                <YouTubePlayer videoId={current.youtube_id} autoplay dark playerRef={playerRef} testId="dodo-player" />
                <p data-testid="dodo-stop-info" className="text-center text-white/50">{t("dodo.stopsAt", { time: stopTime })}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center pt-4">
                <Bini size={130} mood="sleep" data-testid="dodo-bini-sleeping" />
                <p className="mt-4 text-xl text-white/70">{t("dodo.hint")}</p>
              </div>
            )}

            <section className="mt-8 rounded-[32px] bg-[#1C2541] p-5" data-testid="dodo-timer">
              <p className="font-semibold text-white/60">{t("dodo.timer")}</p>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {TIMERS.map((m) => (
                  <button key={m} type="button" onClick={() => pickTimer(m)} data-testid={`timer-${m}-min-btn`} aria-pressed={minutes === m}
                    className={`min-h-[60px] rounded-full text-lg font-bold border-2 ${minutes === m ? "bg-[#F4E3A0] text-[#0B132B] border-[#F4E3A0]" : "border-white/15 text-white/70"}`}>
                    {t("dodo.minutes", { n: m })}
                  </button>
                ))}
              </div>
            </section>

            <section className="mt-6 space-y-3" data-testid="dodo-song-list">
              {songs.length === 0 && <p className="text-white/50 text-center">{t("dodo.empty")}</p>}
              {songs.map((s) => (
                <button key={s.id} type="button" onClick={() => start(s)} data-testid={`dodo-song-${s.slug}`}
                  className={`w-full min-h-[72px] flex items-center gap-4 rounded-[28px] px-5 text-left text-lg font-semibold border-2 ${current?.id === s.id ? "border-[#F4E3A0]/60 bg-[#1C2541]" : "border-transparent bg-[#1C2541]/70"}`}>
                  <Bini size={40} mood="sleep" /> <span className="flex-1">{s.title}</span>
                </button>
              ))}
              {current && (
                <button type="button" onClick={() => { playerRef.current?.stopVideo?.(); setCurrent(null); setStopAt(null); }}
                  data-testid="dodo-stop-btn" className="w-full min-h-[60px] flex items-center justify-center gap-2 rounded-full border-2 border-white/15 text-white/60">
                  <Power className="w-5 h-5" /> Stop
                </button>
              )}
            </section>
          </>
        )}
      </main>
      <Footer dark />
    </div>
  );
}
